// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import Layout from "./components/Layout";
import PrivateRoute from "./components/PrivateRoute";

// Contexts
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import { MarketAnalysisProvider } from "./contexts/MarketAnalysisContext";

// Pages
import ChatPage from "./pages/ChatPage";
import MarketAnalysisPage from "./pages/MarketAnalysisPage";
import StrategiesPage from "./pages/StrategiesPage";
import HelpPage from "./pages/HelpPage";
import AgentIA from "./pages/AgentIA";
import BacktestVitrine from "./pages/BacktestVitrine";
import SSOPage from "./pages/SSOPage";
import CopyTradingPage from "./pages/CopyTradingPage";
import JournalAutoPage from "./pages/JournalAutoPage";
import ExecutionPage from "./pages/ExecutionPage";
import ChallengePage from "./pages/ChallengePage";

import { FULLMARGIN_PROFILE_URL } from "./utils/fullmargin";

import "./styles/App.css";
import "./styles/Header.css";

// Ancienne page « Compte » supprimée : le profil se gère sur FullMargin.
function FullMarginProfileRedirect() {
  React.useEffect(() => {
    window.location.replace(FULLMARGIN_PROFILE_URL);
  }, []);
  return null;
}

function AppRoutes() {
  return (
    <Layout>
      <Routes>
        {/* SSO */}
        <Route path="/sso" element={<SSOPage />} />

        {/* Chat IA */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <ChatPage />
            </PrivateRoute>
          }
        />

        {/* Analyse de marché */}
        <Route
          path="/market-analysis"
          element={
            <PrivateRoute>
              <MarketAnalysisPage />
            </PrivateRoute>
          }
        />

        {/* Stratégies */}
        <Route
          path="/strategies"
          element={
            <PrivateRoute>
              <StrategiesPage />
            </PrivateRoute>
          }
        />

        {/* Challenge */}
        <Route
          path="/challenge"
          element={
            <PrivateRoute>
              <ChallengePage />
            </PrivateRoute>
          }
        />

        {/* Compte utilisateur : géré uniquement sur FullMargin (compte unique).
            La route est gardée pour renvoyer les anciens liens / favoris. */}
        <Route path="/account" element={<FullMarginProfileRedirect />} />

        {/* Paramètres : page supprimée. Thème et langue sont dans le menu du
            haut, le reste se règle sur FullMargin. Anciens liens → accueil. */}
        <Route path="/settings" element={<Navigate to="/" replace />} />

        {/* Aide : hub + une page par guide (/help/<slug>), pour pouvoir pointer
            un bouton « Comment ça marche ? » depuis chaque fonctionnalité. */}
        <Route
          path="/help"
          element={
            <PrivateRoute>
              <HelpPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/help/:slug"
          element={
            <PrivateRoute>
              <HelpPage />
            </PrivateRoute>
          }
        />

        {/* Journal automatique */}
        <Route
          path="/journal-auto"
          element={
            <PrivateRoute>
              <JournalAutoPage />
            </PrivateRoute>
          }
        />

        {/* Exécution */}
        <Route
          path="/execution"
          element={
            <PrivateRoute>
              <ExecutionPage />
            </PrivateRoute>
          }
        />

        {/* Copy Trading */}
        <Route
          path="/copy-trading"
          element={
            <PrivateRoute>
              <CopyTradingPage />
            </PrivateRoute>
          }
        />

        {/* Agent IA : accès autorisé avec agent_token seul */}
        <Route
          path="/agent"
          element={
            <PrivateRoute agentOK>
              <AgentIA />
            </PrivateRoute>
          }
        />

        {/* Backtest — vitrine outil marketplace + licence */}
        <Route
          path="/backtest"
          element={
            <PrivateRoute>
              <BacktestVitrine />
            </PrivateRoute>
          }
        />

        {/* Compatibilité ancienne route */}
        <Route path="/replay" element={<Navigate to="/backtest" replace />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <MarketAnalysisProvider>
            <AppRoutes />
          </MarketAnalysisProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}